import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.setToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Initialize token from localStorage
const savedToken = localStorage.getItem('token');
if (savedToken) {
  api.setToken(savedToken);
}

// Handle 401 errors (unauthorized) - clear token and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      api.setToken(null);
      // Redirect to login will be handled by the app
    }
    return Promise.reject(error);
  }
);

export const API = {
  // Set authentication token
  setToken: (token) => api.setToken(token),

  // Authentication
  login: (username, password) => 
    api.post('/login', { username, password }).then(res => res.data),
  
  register: (username, email, password) => 
    api.post('/register', { username, email, password }).then(res => res.data),
  
  getMe: () => api.get('/me').then(res => res.data),

  // Health check
  getHealth: () => api.get('/health').then(res => res.data),

  // Current stock status
  getCurrentStock: () => api.get('/current-stock').then(res => res.data),

  // Daily summary
  getDailySummary: (days = 30, store = null) => {
    const params = { days };
    if (store) params.store = store;
    return api.get('/daily-summary', { params }).then(res => res.data);
  },

  // Store performance
  getStorePerformance: () => api.get('/store-performance').then(res => res.data),

  // Item performance
  getItemPerformance: (limit = 50, startDate = null, endDate = null) => {
    const params = { limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get('/item-performance', { params }).then(res => res.data);
  },

  // Out of stock events
  getOutOfStock: (hours = 24, store = null) => {
    const params = { hours };
    if (store) params.store = store;
    return api.get('/out-of-stock', { params }).then(res => res.data);
  },

  // Time windows
  getTimeWindows: (days = 7, store = null, date = null) => {
    const params = { days };
    if (store) params.store = store;
    if (date) {
      params.date = date;
      delete params.days; // Use date instead of days if date is provided
    }
    return api.get('/time-windows', { params }).then(res => res.data);
  },

  // Average outage heatmap
  getAverageOutage: (store = null, startDate = null, endDate = null) => {
    const params = {};
    if (store) params.store = store;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get('/average-outage', { params }).then(res => res.data);
  },

  // Weekly trends
  getWeeklyTrends: (weeks = 12, store = null) => {
    const params = { weeks };
    if (store) params.store = store;
    return api.get('/weekly-trends', { params }).then(res => res.data);
  },

  // Stores list
  getStores: () => api.get('/stores').then(res => res.data),

  // Weekly sales store names
  getWeeklySalesStores: () => api.get('/weekly-sales-stores').then(res => res.data),

  // Items list
  getItems: () => api.get('/items').then(res => res.data),

  // Daily sales
  getDailySales: (days = null, store = null, startDate = null, endDate = null) => {
    const params = {};
    // Only include days if startDate/endDate are not provided
    if (days && !startDate && !endDate) {
      params.days = days;
    }
    if (store) params.store = store;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get('/daily-sales', { params }).then(res => res.data);
  },

  // Daily sales by dining option (non-catering breakdown: Dine In, Take Out, etc.)
  getDailySalesByDiningOption: (days = null, store = null, startDate = null, endDate = null) => {
    const params = {};
    if (days && !startDate && !endDate) {
      params.days = days;
    }
    if (store) params.store = store;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get('/daily-sales-by-dining-option', { params }).then(res => res.data);
  },

  // Weekly sales
  getWeeklySales: (store = null, years = null) => {
    const params = {};
    if (store) params.store = store;
    if (years) params.years = Array.isArray(years) ? years.join(',') : years;
    return api.get('/weekly-sales', { params }).then(res => res.data);
  },

  // Weekly sales years
  getWeeklySalesYears: () => api.get('/weekly-sales-years').then(res => res.data),

  // Login logs (Admin only)
  getLoginLogs: (limit = 100, username = null, type = null, startDate = null, endDate = null) => {
    const params = { limit };
    if (username) params.username = username;
    if (type) params.type = type;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get('/login-logs', { params }).then(res => res.data);
  },

  // Agent chat
  agentChat: (question, sessionId = null) => {
    const body = { question };
    if (sessionId) body.session_id = sessionId;
    return api.post('/agent/chat', body).then(res => res.data);
  },

  // Agent chat with stream events (progress + delta + final)
  agentChatStream: async (question, sessionId = null, onEvent = null) => {
    const body = { question };
    if (sessionId) body.session_id = sessionId;

    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/agent/chat/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok || !response.body) {
      const detail = await response.text();
      throw new Error(detail || `Streaming request failed (${response.status})`);
    }

    const decoder = new TextDecoder();
    const reader = response.body.getReader();
    let buffer = '';
    let answer = '';
    let finalSessionId = sessionId;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const payload = line.slice(6).trim();
        if (!payload) continue;
        let eventData;
        try {
          eventData = JSON.parse(payload);
        } catch {
          continue;
        }
        if (eventData.type === 'delta') {
          answer += eventData.chunk || '';
        }
        if (eventData.type === 'final') {
          finalSessionId = eventData.session_id || finalSessionId;
        }
        if (onEvent) {
          onEvent(eventData, answer);
        }
      }
    }
    return { answer, session_id: finalSessionId };
  },
};

export default api;

